import { AxiosError } from 'axios';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import { AutomationError, AutomationErrorCodes } from 'api/integrations';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { getSalesforceAutomationUrl } from 'utils/urls';

export const useAutomationMutationHandler = (integrationId: number) => {
  const history = useHistory();

  const errorTitle = useTranslation('error');
  const successTitle = useTranslation('success');
  const successMessage = useTranslation(
    'settings.integrations.sf.automation.create.success.message',
  );
  const updateSuccessMessage = useTranslation(
    'settings.integrations.sf.automation.updated.success.message',
  );
  const errorMessage = useTranslation(
    'settings.integrations.sf.automation.create.error.message',
  );
  const duplicateNameErrorTitle = useTranslation(
    'settings.integrations.sf.automation.create.error.conflicting_field_value.title',
  );
  const duplicateNameErrorMessage = useTranslation(
    'settings.integrations.sf.automation.create.error.conflicting_field_value.message',
  );

  const onSaveSuccess = useCallback(() => {
    history.push(getSalesforceAutomationUrl(integrationId.toString()));

    toastService({
      title: successTitle,
      message: successMessage,
    }).success();
  }, [integrationId, history, successTitle, successMessage]);

  const onUpdateSuccess = useCallback(() => {
    history.push(getSalesforceAutomationUrl(integrationId.toString()));

    toastService({
      title: successTitle,
      message: updateSuccessMessage,
    }).success();
  }, [integrationId, history, successTitle, updateSuccessMessage]);

  const onError = useCallback(
    (errors: AxiosError<{ errors: AutomationError[] }>) => {
      const duplicateError = errors.response?.data?.errors?.find(
        (error) =>
          error.code === AutomationErrorCodes.ConflictingAutomationName,
      );

      if (duplicateError) {
        toastService({
          title: duplicateNameErrorTitle,
          message: duplicateNameErrorMessage,
        }).error();
      } else {
        toastService({
          title: errorTitle,
          message: errorMessage,
        }).error();
      }
    },
    [
      duplicateNameErrorMessage,
      duplicateNameErrorTitle,
      errorMessage,
      errorTitle,
    ],
  );

  return useMemo(
    () => ({ onSaveSuccess, onUpdateSuccess, onError }),
    [onSaveSuccess, onUpdateSuccess, onError],
  );
};
